var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/MaestroThemeProvider/MaestroThemeProvider.tsx
import React from "react";
import { ThemeProvider } from "styled-components";

// src/themes/types.ts
var Breakpoints;
(function(Breakpoints2) {
  Breakpoints2[Breakpoints2["xs"] = 320] = "xs";
  Breakpoints2[Breakpoints2["sm"] = 480] = "sm";
  Breakpoints2[Breakpoints2["ms"] = 600] = "ms";
  Breakpoints2[Breakpoints2["md"] = 768] = "md";
  Breakpoints2[Breakpoints2["ml"] = 960] = "ml";
  Breakpoints2[Breakpoints2["lg"] = 1024] = "lg";
  Breakpoints2[Breakpoints2["xl"] = 1280] = "xl";
  Breakpoints2[Breakpoints2["xxl"] = 1330] = "xxl";
  Breakpoints2[Breakpoints2["jb"] = 1440] = "jb";
})(Breakpoints || (Breakpoints = {}));

// src/themes/defaultTheme.ts
import { css as css2 } from "styled-components";

// src/utils/assertions.ts
var isFunction = /* @__PURE__ */ __name((value) => typeof value === "function", "isFunction");
var __DEV__ = process.env.NODE_ENV !== "production";
var __TEST__ = process.env.NODE_ENV === "test";

// src/utils/theme.tsx
import { useContext } from "react";
import { ThemeContext } from "styled-components";
import css from "@styled-system/css";
import { rgba } from "polished";
var get = /* @__PURE__ */ __name((obj, path, defaultValue = null) => {
  const result = String.prototype.split.call(path, /[,[\].]+?/).filter(Boolean).reduce((res, key) => res !== null && res !== void 0 ? res[key] : res, obj);
  return result === void 0 || result === obj ? defaultValue : result;
}, "get");
var themeGet = /* @__PURE__ */ __name((props, path) => {
  const value = get(props.theme, path);
  return isFunction(value) ? value(props) : value;
}, "themeGet");
var stripUnit = /* @__PURE__ */ __name((str) => parseFloat(str), "stripUnit");
var token = /* @__PURE__ */ __name((path) => (props) => themeGet(props, path), "token");
var color = /* @__PURE__ */ __name((path, opacity2 = 1) => (props) => rgba(themeGet(props, `colors.${path}`), opacity2), "color");
var opacity = /* @__PURE__ */ __name((path) => (props) => themeGet(props, `opacities.${path}`), "opacity");
var palette = color;
var baseUnit = /* @__PURE__ */ __name((multiplier) => ({ theme }) => theme.baseUnit(multiplier), "baseUnit");
var spacing = /* @__PURE__ */ __name((size, options) => ({ theme }) => theme.spacing(size, options), "spacing");
var fontSize = /* @__PURE__ */ __name((size) => (props) => themeGet(props, `fontSizes.${size}`), "fontSize");
var lineHeight = /* @__PURE__ */ __name((size) => (props) => themeGet(props, `lineHeights.${size}`), "lineHeight");
var fontWeight = /* @__PURE__ */ __name((weight) => (props) => themeGet(props, `fontWeights.${weight}`), "fontWeight");
var radius = /* @__PURE__ */ __name((radius2) => (props) => themeGet(props, `radii.${radius2}`), "radius");
var shadow = /* @__PURE__ */ __name((shadow2) => (props) => themeGet(props, `shadows.${shadow2}`), "shadow");
var component = /* @__PURE__ */ __name((path) => (props) => themeGet(props, `components.${path}`), "component");
var border = /* @__PURE__ */ __name((type) => (props) => themeGet(props, `borders.${type}`), "border");
var zIndex = /* @__PURE__ */ __name((zIndex2) => (props) => {
  return Number(themeGet(props, `zIndices.${zIndex2}`));
}, "zIndex");
var breakpoint = /* @__PURE__ */ __name((breakpoint3) => ({ theme }) => theme.breakpoint(breakpoint3), "breakpoint");
var getThemeModeValue = /* @__PURE__ */ __name((props, values) => {
  const mode = props && props.theme && props.theme.mode;
  const themeValue = typeof mode === "function" ? mode(values) : values[mode];
  return typeof themeValue === "function" ? themeValue(props) : themeValue;
}, "getThemeModeValue");
var themeMode = /* @__PURE__ */ __name((values) => (props) => getThemeModeValue(props, values), "themeMode");
var parseStyles = /* @__PURE__ */ __name((styles) => (props) => {
  if (!styles) {
    return;
  }
  return Object.entries(styles).reduce((acc, [key, value]) => {
    if (typeof value === "object") {
      if (key in props && typeof props[key] === "boolean" && !!props[key]) {
        acc = __spreadValues(__spreadValues({}, acc), parseStyles(value)(props));
        return acc;
      }
      acc[key] = parseStyles(value)(props);
      return acc;
    }
    acc[key] = typeof value === "function" ? value(props) : value;
    return acc;
  }, {});
}, "parseStyles");
var extractStyles = /* @__PURE__ */ __name((props, variantValues, theme) => {
  const variantStyles = typeof variantValues === "function" ? variantValues(props) : variantValues;
  const flattenedValues = parseStyles(variantStyles)(props);
  return css(flattenedValues)(theme);
}, "extractStyles");
var variant = /* @__PURE__ */ __name(({ prop, compoundProp, variants }) => (props) => {
  var _a;
  const { theme } = props;
  let compiledStyles = {};
  const propValue = props[prop];
  const variantValues = variants == null ? void 0 : variants[propValue];
  const extractedStyles = extractStyles(props, variantValues, theme);
  compiledStyles = __spreadValues(__spreadValues({}, compiledStyles), extractedStyles);
  if (compoundProp) {
    const compoundPropValue = props[compoundProp];
    const variantValues2 = (_a = variants == null ? void 0 : variants[propValue]) == null ? void 0 : _a[compoundPropValue];
    const extractedStyles2 = extractStyles(props, variantValues2, theme);
    compiledStyles = __spreadValues(__spreadValues({}, compiledStyles), extractedStyles2);
  }
  const finalStyles = Object.entries(compiledStyles).reduce((acc, [key, value]) => {
    const x = isFunction(value) ? value(props) : value;
    acc[key] = x;
    return acc;
  }, {});
  const finalCompileStyles = css(finalStyles)(theme);
  return finalCompileStyles;
}, "variant");
var useStyles = /* @__PURE__ */ __name(({ styles, props }) => {
  const themeContext = useContext(ThemeContext);
  return styles(__spreadValues({
    theme: themeContext
  }, props));
}, "useStyles");

// src/themes/defaultTheme.ts
var baseFontSize = "15px";
var containers = {
  xs: "320px",
  sm: "480px",
  ms: "600px",
  md: "768px",
  ml: "960px",
  lg: "1024px",
  xl: "1280px",
  xxl: "1330px",
  jb: "1440px"
};
var BASE_UNIT = 4;
var BASE_UNIT_REM = 4 / stripUnit(baseFontSize);
var baseUnit2 = /* @__PURE__ */ __name((multiplier) => `${multiplier * BASE_UNIT}px`, "baseUnit");
var space = {
  none: baseUnit2(0),
  xxs: baseUnit2(1),
  xs: baseUnit2(2),
  sm: baseUnit2(3),
  md: baseUnit2(4),
  lg: baseUnit2(5),
  xl: baseUnit2(6),
  xxl: baseUnit2(9),
  jb: baseUnit2(12),
  mg: baseUnit2(18)
};
var spacing2 = /* @__PURE__ */ __name((amount, options) => {
  const unitless = options && options.unitless;
  if (amount === "auto") {
    return amount;
  }
  if (typeof amount === "string" && amount in space) {
    return unitless ? stripUnit(space[amount]) : space[amount];
  }
  const value = amount;
  return unitless ? stripUnit(baseUnit2(value)) : baseUnit2(value);
}, "spacing");
var fontFamily = {
  base: `'Sailec', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`
};
var fontSizes = {
  xs: "0.7333334rem",
  sm: "0.8666667rem",
  base: "1rem",
  md: "1rem",
  lg: "1.2rem",
  xl: "1.6rem",
  xxl: "2rem",
  xxxl: "2.4rem",
  jb: "3.2rem",
  mg: "4rem"
};
var fontWeights = {
  light: 300,
  regular: 400,
  bold: 700,
  extrabold: 900
};
var lineHeights = {
  xs: baseUnit2(4),
  sm: baseUnit2(5),
  base: baseUnit2(6),
  md: baseUnit2(6),
  lg: baseUnit2(7),
  xl: baseUnit2(9),
  xxl: baseUnit2(10),
  xxxl: baseUnit2(13),
  jb: baseUnit2(15),
  mg: baseUnit2(16)
};
var breakpoints = [
  `${Breakpoints.xs}px`,
  `${Breakpoints.sm}px`,
  `${Breakpoints.ms}px`,
  `${Breakpoints.md}px`,
  `${Breakpoints.ml}px`,
  `${Breakpoints.lg}px`,
  `${Breakpoints.xl}px`,
  `${Breakpoints.xxl}px`,
  `${Breakpoints.jb}px`
];
breakpoints["xs"] = breakpoints[0];
breakpoints["sm"] = breakpoints[1];
breakpoints["ms"] = breakpoints[2];
breakpoints["md"] = breakpoints[3];
breakpoints["ml"] = breakpoints[4];
breakpoints["lg"] = breakpoints[5];
breakpoints["xl"] = breakpoints[6];
breakpoints["xxl"] = breakpoints[7];
breakpoints["jb"] = breakpoints[8];
var breakpoint2 = /* @__PURE__ */ __name((breakpoint3 = "md", direction = "down") => (literals, ...args) => css2`
            @media (${direction === "down" ? "max" : "min"}-width: ${breakpoints[breakpoint3]}) {
                ${css2(literals, ...args)}
            }
        `, "breakpoint");
var radii = {
  sm: "2px",
  base: "4px",
  lg: "8px",
  round: "100rem"
};
var palette2 = {
  primary: {
    300: "#84dfdf",
    400: "#5ad5d5",
    500: "#32cbcb",
    600: "#2db6b6",
    700: "#28a2a2"
  },
  secondary: {
    300: "#3c6ed7",
    400: "#245dd2",
    500: "#0c4bce",
    600: "#0a43b9",
    700: "#093ca4"
  },
  neutral: {
    0: "#ffffff",
    50: "#f9fafb",
    100: "#f4f6f8",
    200: "#dfe3e9",
    300: "#c5cdd5",
    400: "#adb7bf",
    500: "#6c7782",
    600: "#5a6774",
    700: "#435261",
    800: "#2c3c4d",
    900: "#142739",
    1e3: "#0c1b2b"
  },
  danger: {
    300: "#ea5b70",
    400: "#e7435c",
    500: "#e11534",
    600: "#ca122e",
    700: "#b41029"
  },
  warning: {
    300: "#f6e07f",
    400: "#f3d44c",
    500: "#eec200",
    600: "#eab102",
    700: "#dba602"
  },
  success: {
    300: "#84dfdf",
    400: "#5ad5d5",
    500: "#32cbcb",
    600: "#2db6b6",
    700: "#28a2a2"
  },
  facebook: {
    500: "#4267b2",
    700: "#35528e"
  },
  twitter: {
    500: "#00aced",
    700: "#008abe"
  },
  bronze: {
    300: "#BF8A59",
    400: "#A66F40",
    500: "#875733",
    600: "#5E3F22",
    700: "#3A2912"
  },
  silver: {
    300: "#B7BDC8",
    400: "#9CA4AF",
    500: "#7D8794",
    600: "#68707E",
    700: "#505663"
  },
  gold: {
    300: "#BFB58B",
    400: "#AD9E6C",
    500: "#A3905B",
    600: "#74683F",
    700: "#534D2C"
  },
  platinum: {
    0: "#ffffff",
    50: "#f9fafb",
    100: "#f4f6f8",
    200: "#dfe3e9",
    300: "#c5cdd5",
    400: "#adb7bf",
    500: "#6c7782",
    600: "#5a6774",
    700: "#435261",
    800: "#2c3c4d",
    900: "#142739",
    1e3: "#0c1b2b"
  }
};
var borders = {
  base: themeMode({
    light: `1px solid ${palette2.neutral[300]}`,
    dark: `1px solid ${palette2.neutral[600]}`
  }),
  hover: themeMode({
    light: `1px solid ${palette2.neutral[500]}`,
    dark: `1px solid ${palette2.neutral[400]}`
  }),
  subtle: themeMode({
    light: `1px solid ${palette2.neutral[200]}`,
    dark: `1px solid ${palette2.neutral[700]}`
  })
};
var colors = __spreadValues({
  text: {
    base: themeMode({
      light: palette2.neutral[900],
      dark: palette2.neutral[50]
    }),
    // @deprecated: use `accent` instead
    high: themeMode({
      light: palette2.primary[500],
      dark: palette2.primary[500]
    }),
    accent: themeMode({
      light: palette2.primary[500],
      dark: palette2.primary[500]
    }),
    // @deprecated: use `subtle` instead
    low: themeMode({
      light: palette2.neutral[500],
      dark: palette2.neutral[400]
    }),
    subtle: themeMode({
      light: palette2.neutral[500],
      dark: palette2.neutral[400]
    }),
    muted: themeMode({
      light: palette2.neutral[400],
      dark: palette2.neutral[500]
    }),
    inverted: themeMode({
      light: palette2.neutral[0],
      dark: palette2.neutral[1e3]
    })
  },
  background: {
    base: themeMode({
      light: palette2.neutral[0],
      dark: palette2.neutral[1e3]
    }),
    high: palette2.danger[500],
    low: palette2.danger[500]
  },
  border: {
    base: palette2.neutral[200],
    high: palette2.danger[500],
    low: palette2.danger[500]
  },
  surface: themeMode({
    light: palette2.neutral[0],
    dark: palette2.neutral[800]
  })
}, palette2);
var shadows = {
  0: "none",
  1: "0 2px 4px 0 rgba(0, 0, 0, .04), 0 3px 4px 0 rgba(0, 0, 0, .04), 0 1px 5px 0 rgba(0, 0, 0, .08)",
  2: "0 6px 10px 0 rgba(0, 0, 0, .04), 0 1px 18px 0 rgba(0, 0, 0, .04), 0 3px 5px 0 rgba(0, 0, 0, .08)",
  3: "0 12px 17px 2px rgba(0, 0, 0, .04), 0 5px 22px 4px rgba(0, 0, 0, .04), 0 7px 8px 0 rgba(0, 0, 0, .08)"
};
var components = {};
var modes = [
  "light",
  "dark"
];
var defaultMode = modes[1];
var zIndices = {
  absoluteTop: 2147483638,
  alert: 4e3,
  modal: 3e3,
  overlay: 2e3,
  subOverlay: 1e3,
  base: 0
};
var opacities = {
  invisible: 0,
  transparent: 0.16,
  shadow: 0.32,
  subtle: 0.48,
  translucent: 0.64,
  overlay: 0.8,
  opaque: 1
};
var defaultTheme = {
  BASE_UNIT,
  BASE_UNIT_REM,
  baseUnit: baseUnit2,
  containers,
  spacing: spacing2,
  space,
  palette: palette2,
  colors,
  fontFamily,
  fontSizes,
  fontWeights,
  baseFontSize,
  lineHeights,
  breakpoints,
  breakpoint: breakpoint2,
  borders,
  radii,
  shadows,
  components,
  modes,
  defaultMode,
  zIndices,
  opacities
};

// src/MaestroThemeProvider/MaestroThemeProvider.tsx
var MaestroThemeProvider = /* @__PURE__ */ __name(({ theme = defaultTheme, mode = "dark", children }) => {
  return /* @__PURE__ */ React.createElement(ThemeProvider, {
    theme: __spreadProps(__spreadValues({}, theme), {
      mode
    })
  }, children);
}, "MaestroThemeProvider");
MaestroThemeProvider.displayName = "Maestro.ThemeProvider";

// src/MaestroThemeProvider/ColorMode.tsx
import React2 from "react";
import { ThemeProvider as ThemeProvider2 } from "styled-components";
var setTheme = /* @__PURE__ */ __name((modeOption) => (theme) => {
  if (!theme || theme && !theme.mode) {
    throw new Error("The `ColorMode` component expected a theme with a mode property but none was provided.");
  }
  let { mode } = theme;
  switch (modeOption) {
    case "light":
      mode = "light";
      break;
    case "dark":
      mode = "dark";
      break;
    case "invert":
      if (mode === "dark") {
        mode = "light";
      } else {
        mode = "dark";
      }
      break;
    default:
      break;
  }
  return __spreadProps(__spreadValues({}, theme), {
    mode
  });
}, "setTheme");
var ColorMode = /* @__PURE__ */ __name(({ mode, children }) => {
  return /* @__PURE__ */ React2.createElement(ThemeProvider2, {
    theme: setTheme(mode)
  }, children);
}, "ColorMode");

// src/MaestroThemeProvider/GlobalStyle.tsx
import React3, { useEffect, useLayoutEffect } from "react";
import { createGlobalStyle, css as css3 } from "styled-components";
var canUseDOM = /* @__PURE__ */ __name(() => !!(typeof window !== "undefined" && window.document && window.document.createElement), "canUseDOM");
var isBrowser = canUseDOM();
var useEnhancedEffect = isBrowser ? useLayoutEffect : useEffect;
var GlobalStyleCSS = css3`
    *,
    *:before,
    *:after {
        box-sizing: inherit;
    }

    :root {
        /* This variable is used by old webapp to adapt its layout */
        --navbar-height: 64px;
    }

    :focus:not(:focus-visible) {
        outline: 0;
    }

    html {
        /* stylelint-disable */
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        -moz-text-size-adjust: 100%;
        -ms-text-size-adjust: 100%;
        -webkit-text-size-adjust: 100%;
        font-size: ${({ theme }) => theme.baseFontSize};
        box-sizing: border-box;
    }

    body {
        color: ${color("text.base")};
        background-color: ${color("background.base")};
        font-family: ${({ theme }) => theme.fontFamily.base};
        line-height: ${({ theme }) => theme.lineHeights.base};
        font-size: ${({ theme }) => theme.baseFontSize};
        font-weight: ${fontWeight("regular")};
    }

    html,
    body {
        margin: 0;
        padding: 0;
        height: 100%;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        margin: 0;
    }

    figure {
        margin: 0;
        padding: 0;
    }

    /* Remove margin and padding */
    ul,
    ol,
    li {
        margin: 0;
        padding: 0;
        list-style: none;
    }

    /* Inherit typography settings */
    input,
    textarea,
    select,
    button {
        font: inherit;
        letter-spacing: inherit;
    }

    a {
        color: ${color("text.base")};
    }

    /* Remove extra styles for button */
    button {
        color: inherit;
        border-radius: 0;
        border: none;
        background-color: transparent;
    }

    /* For JS interaction - Prevent the child of a button to be targeted */
    button * {
        pointer-events: none;
    }

    /* Default table styles */
    table {
        width: 100%;
    }

    [data-color-mode='light'] {
        color: ${color("neutral.900")};
        background-color: ${color("neutral.0")};

        a {
            color: ${color("neutral.900")};
        }
    }

    [data-color-mode='dark'] {
        color: ${color("neutral.50")};
        background-color: ${color("neutral.1000")};

        a {
            color: ${color("neutral.50")};
        }
    }
`;
var InternalGlobalStyle = createGlobalStyle`
    ${GlobalStyleCSS};
`;
var insertCssImports = /* @__PURE__ */ __name((enabled) => {
  if (document.getElementById("global-css-imports") || !enabled) {
    return;
  }
  const head = document.head || document.getElementsByTagName("head")[0];
  const styleElement = document.createElement("style");
  styleElement.id = "global-css-imports";
  head.insertBefore(styleElement, head.firstChild);
  styleElement.appendChild(document.createTextNode(`
            @import 'https://cdnjs.cloudflare.com/ajax/libs/normalize/8.0.1/normalize.min.css';
            @import 'https://cdn.landr.com/fonts/Sailec/style.css';
`));
}, "insertCssImports");
var GlobalStyle = /* @__PURE__ */ __name(({ withCssImports = true }) => {
  useEnhancedEffect(() => insertCssImports(withCssImports), []);
  return /* @__PURE__ */ React3.createElement(InternalGlobalStyle, null);
}, "GlobalStyle");
export {
  Breakpoints,
  ColorMode,
  GlobalStyle,
  MaestroThemeProvider,
  __DEV__,
  __TEST__,
  baseUnit,
  border,
  breakpoint,
  canUseDOM,
  color,
  component,
  defaultTheme,
  fontSize,
  fontWeight,
  get,
  isBrowser,
  isFunction,
  lineHeight,
  opacity,
  palette,
  radius,
  setTheme,
  shadow,
  spacing,
  stripUnit,
  themeMode,
  token,
  useEnhancedEffect,
  useStyles,
  variant,
  zIndex
};
//# sourceMappingURL=index.mjs.map